import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  DoneAll as DoneAllIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSocket } from '../../context/SocketContext';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  selectNotifications,
  selectUnreadCount,
  selectNotificationLoading,
  addNotification,
} from '../../redux/slices/notificationSlice';
import { format } from 'date-fns';

const NotificationBell = () => {
  const dispatch = useDispatch();
  const { socket, isConnected } = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);

  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const isLoading = useSelector(selectNotificationLoading);

  // ============================================
  // FETCH NOTIFICATIONS ON MOUNT
  // ============================================
  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, limit: 20 }));
  }, [dispatch]);

  // ============================================
  // SOCKET EVENT: NEW NOTIFICATION
  // ============================================
  useEffect(() => {
    if (!socket) return;

    socket.on('new-notification', (data) => {
      if (data.notification) {
        dispatch(addNotification(data.notification));
      }
    });

    socket.on('unread-count', (data) => {
      // Update unread count
    });

    return () => {
      socket.off('new-notification');
      socket.off('unread-count');
    };
  }, [socket, dispatch]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // Refresh notifications on open
    dispatch(fetchNotifications({ page: 1, limit: 20 }));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (notificationId) => {
    await dispatch(markNotificationRead(notificationId));
    if (socket) {
      socket.emit('notification-read', {
        restaurantId: JSON.parse(localStorage.getItem('restaurantApp_user') || '{}')?._id,
        notificationId,
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllNotificationsRead());
    if (socket) {
      const restaurantId = JSON.parse(localStorage.getItem('restaurantApp_user') || '{}')?._id;
      socket.emit('notifications-read-all', { restaurantId });
    }
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    await dispatch(deleteNotification(notificationId));
  };

  const getIcon = (type) => {
    const icons = {
      NEW_ORDER: '🆕',
      ORDER_STATUS_CHANGED: '🔄',
      ORDER_CANCELLED: '❌',
      PAYMENT_RECEIVED: '💰',
      PAYMENT_FAILED: '💳',
      OFFER_CREATED: '🎉',
      OFFER_UPDATED: '📝',
      OFFER_DELETED: '❌',
      OFFER_STATUS_CHANGED: '🔄',
      CATEGORY_CREATED: '📂',
      CATEGORY_UPDATED: '📝',
      CATEGORY_DELETED: '❌',
      CATEGORY_STATUS_CHANGED: '🔄',
      MENU_ITEM_ADDED: '🍽️',
      MENU_ITEM_UPDATED: '📝',
      MENU_ITEM_DELETED: '❌',
      MENU_ITEM_TOGGLED: '🔄',
      STOCK_ALERT: '📦',
      LOW_STOCK: '⚠️',
      OUT_OF_STOCK: '❌',
      SYSTEM_ALERT: '⚡',
      GENERAL: '🔔',
    };
    return icons[type] || '🔔';
  };

  return (
    <>
      <IconButton onClick={handleOpen}>
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : 0}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              minWidth: 18,
              height: 18,
            },
          }}
        >
          {isConnected ? (
            <NotificationsActiveIcon sx={{ color: 'var(--primary)' }} />
          ) : (
            <NotificationsIcon />
          )}
        </Badge>
        {isConnected && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#22c55e',
              border: '2px solid white',
            }}
          />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 500,
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Notifications
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} new`}
                color="error"
                size="small"
                sx={{ ml: 1, fontWeight: 600 }}
              />
            )}
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* Loading */}
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={30} sx={{ color: 'var(--primary)' }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: '#d1d5db' }} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              No notifications yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              New notifications will appear here
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, overflow: 'auto' }}>
            {notifications.slice(0, 20).map((notification, index) => (
              <React.Fragment key={notification._id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    p: 2,
                    bgcolor: notification.read ? 'transparent' : 'rgba(255,107,0,0.04)',
                    '&:hover': { bgcolor: 'rgba(255,107,0,0.06)' },
                    transition: 'background 0.2s',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (!notification.read) {
                      handleMarkAsRead(notification._id);
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: notification.read ? '#f3f4f6' : 'rgba(255,107,0,0.1)',
                        width: 44,
                        height: 44,
                      }}
                    >
                      <Typography fontSize="1.2rem">
                        {getIcon(notification.type)}
                      </Typography>
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={notification.read ? 500 : 700}>
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.priority || 'normal'}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            bgcolor: notification.priority === 'high' ? '#fef2f2' : '#f3f4f6',
                            color: notification.priority === 'high' ? '#ef4444' : '#6b7280',
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {notification.message}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(notification.createdAt), 'dd MMM, hh:mm a')}
                          </Typography>
                          {!notification.read && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#FF6B00',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleDelete(notification._id, e)}
                    sx={{ ml: 1, alignSelf: 'flex-start' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <Box
            sx={{
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Button
              size="small"
              onClick={() => {
                handleClose();
                // Navigate to notifications page
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View All Notifications
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;